import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { API_BASE_URL } from '@/api/client'
import type { ReplyAttachmentDraft } from '@/features/communications/types'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import type { QuoteComment } from '../publishing/api'

// Customer comments on a published quote and Sales's answers (USA-1345).

export type CommentFile = components['schemas']['QbCommentFileRead']
type StaffCommentCreate = components['schemas']['QbStaffCommentCreate']

/** Every comment on one item (or on the whole quote), oldest first. */
export type CommentGroup = {
  key: string
  rowRef: string | null
  /** "2: Fryer" for numbered items, the description for options, null for the whole quote. */
  label: string | null
  comments: QuoteComment[]
  latestAt: string
  /** The customer wrote last, so Sales owes an answer. */
  waiting: boolean
}

const isCustomer = (comment: QuoteComment) => comment.actor_id === null

/** Comments by item, in the order they arrived; the server sends them oldest first. */
export function groupComments(comments: QuoteComment[]): CommentGroup[] {
  const groups = new Map<string, QuoteComment[]>()
  for (const comment of comments) {
    const key = comment.row_ref ?? ''
    groups.set(key, [...(groups.get(key) ?? []), comment])
  }
  return [...groups].map(([key, items]) => {
    const latest = items[items.length - 1]!
    return {
      key,
      rowRef: latest.row_ref,
      label: latest.row_label,
      comments: items,
      latestAt: latest.created_at,
      waiting: isCustomer(latest),
    }
  })
}

/** Waiting on an answer first, then the most recent conversation first. */
export function rankGroups(groups: CommentGroup[]): CommentGroup[] {
  return [...groups].sort(
    (a, b) => Number(b.waiting) - Number(a.waiting) || Date.parse(b.latestAt) - Date.parse(a.latestAt),
  )
}

/** How many items wait on an answer from Sales. */
export function waitingCount(comments: QuoteComment[] | undefined) {
  return comments ? groupComments(comments).filter((group) => group.waiting).length : 0
}

/** A photo or file opens straight from the API: its path is signed. */
export function commentFileUrl(file: Pick<CommentFile, 'url'>) {
  return `${API_BASE_URL}${file.url}`
}

/** What went wrong, in the salesperson's words. */
export function answerErrorMessage(error: unknown) {
  if (error instanceof QuoteBuilderApiError) {
    if (error.code === 'quote_not_shared') return 'Publish the quote to answer comments.'
    if (error.code === 'share_closed') return "The customer's link is closed. Publish the quote again to answer here."
    const detail = error.body.detail
    if (detail && typeof detail === 'object' && typeof detail.filename === 'string' && typeof detail.message === 'string') {
      if (detail.message) return detail.message
    }
  }
  return 'Your answer could not be sent. Try again.'
}

function base64Blob(contentBase64: string, contentType: string) {
  const bytes = Uint8Array.from(atob(contentBase64), (character) => character.charCodeAt(0))
  return new Blob([bytes], { type: contentType })
}

export type AnswerInput = { rowRef: string | null; body: string; files: ReplyAttachmentDraft[] }

/** Sales's answer on an item; with files it goes as one upload (up to five, 10 MB each). */
export function useAnswerQuoteComment(quoteId: string) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const queryKey = ['quote-comments', quoteId]
  return useMutation({
    mutationFn: ({ rowRef, body, files }: AnswerInput) => {
      const comment: StaffCommentCreate = { body, row_ref: rowRef }
      if (files.length === 0) {
        return api.request<QuoteComment>(`/quotes/${quoteId}/comments`, {
          method: 'POST',
          body: JSON.stringify(comment),
        })
      }
      const form = new FormData()
      form.append('comment', JSON.stringify(comment))
      files.forEach((file) => form.append('files', base64Blob(file.contentBase64, file.contentType), file.filename))
      return api.request<QuoteComment>(`/quotes/${quoteId}/comments/files`, { method: 'POST', body: form })
    },
    onSuccess: (created) =>
      queryClient.setQueryData<QuoteComment[]>(queryKey, (previous) =>
        previous && !previous.some((comment) => comment.id === created.id) ? [...previous, created] : previous,
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}
